import type { AvailabilityTemplateResponse, DayOfWeek, ReplaceWeeklyTemplateRequest, WorkingHoursDay } from '../../api/types';

/**
 * A doctor's week at one branch, as the editor holds it, and the checks it runs before saving.
 *
 * The server checks very little here: a block must end after it starts (a database constraint), and
 * that is all. Two blocks on one day that overlap, or hours that put the doctor in two branches at
 * once, would both be saved and would both make the slot finder offer times the doctor cannot keep.
 * So the editor refuses them itself, before anything is sent.
 */

/** Saturday first, as the branch week is (ASM-004). */
export const WEEK_DAYS: readonly DayOfWeek[] = [
  'SATURDAY',
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
];

/** One stretch of working time, as `HH:mm` wall time in Cairo. Empty while being typed. */
export interface Block {
  readonly startsAt: string;
  readonly endsAt: string;
}

export type Week = Readonly<Record<DayOfWeek, readonly Block[]>>;

/** A block at another branch, kept with that branch so a clash can name it. */
export interface ElsewhereBlock extends Block {
  readonly branchId: string;
}

export type DayProblem =
  | { readonly kind: 'incomplete' }
  | { readonly kind: 'order' }
  | { readonly kind: 'overlap' }
  | { readonly kind: 'clash'; readonly branchId: string; readonly block: Block };

export function emptyWeek(): Week {
  return Object.fromEntries(WEEK_DAYS.map((day) => [day, []])) as unknown as Week;
}

/** The API sends `HH:mm:ss` (or `HH:mm`); the editor and a time input want `HH:mm`. */
export function hhmm(time: string | undefined): string {
  return time ? time.slice(0, 5) : '';
}

/**
 * The rows still in force today. The API returns end-dated history too, because a utilisation
 * report over last month has to measure last month's rota (REP-05); the editor shows only today's.
 */
export function currentTemplates(
  rows: readonly AvailabilityTemplateResponse[],
  today: string,
): AvailabilityTemplateResponse[] {
  return rows.filter((row) => !row.effectiveUntil || row.effectiveUntil >= today);
}

export function weekAt(rows: readonly AvailabilityTemplateResponse[], branchId: string): Week {
  const week: Record<DayOfWeek, Block[]> = { ...emptyWeek() } as Record<DayOfWeek, Block[]>;
  WEEK_DAYS.forEach((day) => {
    week[day] = rows
      .filter((row) => row.branchId === branchId && row.dayOfWeek === day)
      .map((row) => ({ startsAt: hhmm(row.startsAt), endsAt: hhmm(row.endsAt) }))
      .sort(byStart);
  });
  return week;
}

export function elsewhereOn(
  rows: readonly AvailabilityTemplateResponse[],
  branchId: string,
  day: DayOfWeek,
): ElsewhereBlock[] {
  return rows
    .filter((row) => row.branchId !== branchId && row.dayOfWeek === day)
    .map((row) => ({ branchId: row.branchId ?? '', startsAt: hhmm(row.startsAt), endsAt: hhmm(row.endsAt) }))
    .sort(byStart);
}

/** Everything wrong with one day, worst first; empty means it can be saved. */
export function problemsOn(blocks: readonly Block[], elsewhere: readonly ElsewhereBlock[]): DayProblem[] {
  if (blocks.some((block) => !block.startsAt || !block.endsAt)) {
    return [{ kind: 'incomplete' }];
  }
  if (blocks.some((block) => block.endsAt <= block.startsAt)) {
    return [{ kind: 'order' }];
  }

  const problems: DayProblem[] = [];
  const sorted = [...blocks].sort(byStart);
  if (sorted.some((block, index) => index > 0 && overlaps(sorted[index - 1]!, block))) {
    problems.push({ kind: 'overlap' });
  }
  elsewhere.forEach((other) => {
    if (blocks.some((block) => overlaps(block, other))) {
      problems.push({ kind: 'clash', branchId: other.branchId, block: other });
    }
  });
  return problems;
}

export function weekHasProblems(week: Week, elsewhere: (day: DayOfWeek) => readonly ElsewhereBlock[]): boolean {
  return WEEK_DAYS.some((day) => problemsOn(week[day], elsewhere(day)).length > 0);
}

/**
 * Whether any of the day's hours fall where nothing can be booked: on a day the branch is closed,
 * or before it opens or after it closes. The slot finder clips a doctor's hours to the branch's
 * (`SlotComputation`), so these are not refused, only pointed out.
 */
export function outsideBranch(
  blocks: readonly Block[],
  hours: WorkingHoursDay | undefined,
): 'closed' | 'outside' | null {
  const complete = blocks.filter((block) => block.startsAt && block.endsAt);
  if (complete.length === 0 || !hours) {
    return null;
  }
  if (hours.closed || !hours.opensAt || !hours.closesAt) {
    return 'closed';
  }
  const opens = hhmm(hours.opensAt);
  const closes = hhmm(hours.closesAt);
  return complete.some((block) => block.startsAt < opens || block.endsAt > closes) ? 'outside' : null;
}

/** The first block a day is given: the branch's hours that day, or a working day's worth. */
export function firstBlock(hours: WorkingHoursDay | undefined): Block {
  if (hours && !hours.closed && hours.opensAt && hours.closesAt) {
    return { startsAt: hhmm(hours.opensAt), endsAt: hhmm(hours.closesAt) };
  }
  return { startsAt: '10:00', endsAt: '18:00' };
}

export function sameWeek(a: Week, b: Week): boolean {
  return WEEK_DAYS.every((day) => {
    const left = [...a[day]].sort(byStart);
    const right = [...b[day]].sort(byStart);
    return (
      left.length === right.length &&
      left.every((block, index) => block.startsAt === right[index]?.startsAt && block.endsAt === right[index]?.endsAt)
    );
  });
}

export function toRequest(branchId: string, week: Week): ReplaceWeeklyTemplateRequest {
  return {
    branchId,
    week: WEEK_DAYS.flatMap((day) =>
      [...week[day]].sort(byStart).map((block) => ({
        dayOfWeek: day,
        startsAt: `${block.startsAt}:00`,
        endsAt: `${block.endsAt}:00`,
      })),
    ),
  };
}

export function formatBlock(block: Block): string {
  return `${block.startsAt}–${block.endsAt}`;
}

/** Every date from `from` to `to`, both included, as `YYYY-MM-DD`. */
export function datesBetween(from: string, to: string): string[] {
  const dates: string[] = [];
  const end = Date.parse(`${to}T00:00:00Z`);
  for (let at = Date.parse(`${from}T00:00:00Z`); at <= end; at += 86_400_000) {
    dates.push(new Date(at).toISOString().slice(0, 10));
  }
  return dates;
}

function overlaps(a: Block, b: Block): boolean {
  return a.startsAt < b.endsAt && b.startsAt < a.endsAt;
}

function byStart(a: Block, b: Block): number {
  return a.startsAt.localeCompare(b.startsAt);
}
